import { Segment, ScoreDetails, ApproachParameters } from 'ff/debrief/models';
import { fromUnixTime, getUnixTime } from 'date-fns';
import { getScoreColor, pointsInRange } from 'ff/debrief/utils';
import { FFTheme } from 'ff/debrief/common/StyledComponentThemes';

type Predicate = (segment: SegmentNode) => boolean;

interface Params {
    rootSegment: Segment;
    trackPoints: number[][];
    startTime: number;
    endTime: number;
    isScoreModeEnabled: boolean;
}

class DebriefSegments {
    root: SegmentNode;
    startTime: number;
    endTime: number;

    constructor({
        rootSegment,
        trackPoints,
        startTime,
        endTime,
        isScoreModeEnabled,
    }: Params) {
        // Helper func to traverse raw segment tree
        if (rootSegment === undefined || rootSegment === null) {
            throw new Error(
                'Could not build segment tree. Root undefined or null'
            );
        }
        function createChildNodes(parent: SegmentNode, children: Segment[]) {
            return children.map(child => {
                const childStartTime = startTime + child.startOffsetInSeconds;
                const childEndTime =
                    startTime +
                    child.startOffsetInSeconds +
                    child.durationInSeconds;
                const childNode = new SegmentNode({
                    ...child,
                    startTime: childStartTime,
                    endTime: childEndTime,
                    isScoreModeEnabled: isScoreModeEnabled,
                });
                childNode.parent = parent;
                childNode.points = pointsInRange(
                    trackPoints,
                    childStartTime,
                    childEndTime
                );
                childNode.children = createChildNodes(
                    childNode,
                    child.children
                );
                return childNode;
            });
        }
        const root = new SegmentNode({
            ...rootSegment,
            startTime,
            endTime,
            isScoreModeEnabled,
        });
        root.children = createChildNodes(root, rootSegment.children);
        const frontPaddingSegment = new SegmentNode({
            id: 'frontPadding',
            label: 'frontPadding',
            category: 'padding',
            color: null,
            distance: null,
            score: null,
            scoringDetails: [],
            startOffsetInSeconds: 0,
            startUtc: 0,
            durationInSeconds: root.children.at(0).startOffsetInSeconds,
            startTime: startTime + 1,
            endTime: startTime + root.children.at(0).startOffsetInSeconds,
            isScoreModeEnabled: isScoreModeEnabled,
        });
        const backPaddingSegment = new SegmentNode({
            id: 'backPadding',
            label: 'backPadding',
            category: 'padding',
            color: null,
            distance: null,
            score: null,
            scoringDetails: [],
            startOffsetInSeconds: 0,
            startUtc: 0,
            durationInSeconds:
                endTime - root.children.at(-1).startOffsetInSeconds,
            startTime: startTime + root.children.at(-1).startOffsetInSeconds,
            endTime: endTime,
            isScoreModeEnabled: isScoreModeEnabled,
        });
        frontPaddingSegment.children = [];
        backPaddingSegment.children = [];
        root.children.unshift(frontPaddingSegment);
        root.children.push(backPaddingSegment);
        this.root = root;
        this.startTime = startTime;
        this.endTime = endTime;
    }
    public getSegmentById(id: string): SegmentNode {
        let segment: SegmentNode = null;
        this.traverse(seg => {
            if (seg.id === id) segment = seg;
        });
        return segment;
    }
    public getScoredSegments(): SegmentNode[] {
        const scoredSegments: SegmentNode[] = [];
        this.traverse(seg => seg.score !== null && scoredSegments.push(seg));
        return scoredSegments;
    }
    public getLeafNodes(range?: {
        start?: number | Date;
        end?: number | Date;
    }): SegmentNode[] {
        const rangeTimes = range
            ? {
                  start:
                      typeof range.start === 'number'
                          ? range.start
                          : getUnixTime(range.start),
                  end:
                      typeof range.end === 'number'
                          ? range.end
                          : getUnixTime(range.end),
              }
            : null;
        const leafNodes: SegmentNode[] = [];
        this.traverse(segment => {
            if (segment.children.length === 0 && segment.inRange(rangeTimes)) {
                leafNodes.push(segment);
            }
        });
        // Sort by timestamp (startOffsetInSeconds)
        return leafNodes.sort(
            (a, b) => a.startOffsetInSeconds - b.startOffsetInSeconds
        );
    }
    private traverse(callback: (segment: SegmentNode) => void): void {
        function traverseHelper(segment: SegmentNode) {
            callback(segment);
            segment.children.forEach(traverseHelper);
        }
        traverseHelper(this.root);
    }
}

export class SegmentNode implements Segment {
    category: string;
    color: string;
    distance: string;
    durationInSeconds: number;
    id: string;
    label: string;
    score: number;
    scoringDetails: ScoreDetails[];
    startOffsetInSeconds: number;
    startUtc: number;
    approachParameters: ApproachParameters;
    isScoreModeEnabled: boolean;

    children: SegmentNode[];
    parent: SegmentNode;
    startTime: number;
    endTime: number;

    points: number[][];

    constructor(
        segment: Segment & {
            startTime: number;
            endTime: number;
            isScoreModeEnabled: boolean;
        }
    ) {
        this.category = segment.category;
        this.color = segment.color;
        this.distance = segment.distance;
        this.durationInSeconds = segment.durationInSeconds;
        this.id = segment.id;
        this.label = segment.label;
        this.score = segment.score || null;
        this.scoringDetails = segment.scoringDetails;
        this.startOffsetInSeconds = segment.startOffsetInSeconds;

        this.isScoreModeEnabled = segment.isScoreModeEnabled;
        this.startTime = segment.startTime;
        this.endTime = segment.endTime;
    }

    getColor(): string {
        if (this.score) {
            return this.isScoreModeEnabled
                ? getScoreColor(this.score)
                : FFTheme.blue;
        }
        // Look for parent scores
        for (let curr = this.parent; curr !== undefined; curr = curr.parent) {
            if (curr.score !== null) return getScoreColor(curr.score);
        }
        return '#21C1FA';
    }

    getStartTime(): Date {
        return fromUnixTime(this.startTime);
    }

    getEndTime(): Date {
        return fromUnixTime(this.endTime);
    }

    hasScore(includeParents: boolean = false): boolean {
        if (includeParents) {
            for (
                let curr = this.parent;
                curr !== undefined;
                curr = curr.parent
            ) {
                if (curr.hasScore()) {
                    return true;
                }
            }
        }
        return (
            this.score !== undefined &&
            this.score !== null &&
            typeof this.score === 'number'
        );
    }

    hasScoreDetails(): boolean {
        return (
            this.scoringDetails !== undefined && this.scoringDetails?.length > 0
        );
    }

    hasParentMatching(predicate: Predicate): boolean {
        for (let curr = this.parent; curr !== undefined; curr = curr.parent) {
            if (predicate(curr)) {
                return true;
            }
        }
        return false;
    }

    inRange(range?: { start?: number; end?: number }): boolean {
        if (range === undefined || range === null) {
            return true;
        } else if (range.start !== undefined && range.end === undefined) {
            return this.startTime >= range.start;
        } else if (range.start === undefined && range.end !== undefined) {
            return this.endTime <= range.end;
        } else if (range.start !== undefined && range.end !== undefined) {
            return this.startTime >= range.start && this.endTime <= range.end;
        } else {
            return true;
        }
    }
}

export default DebriefSegments;
